import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BosDurum, Buton, Iskelet, Kart, MetinAlani, Rozet, Secim, Tablo } from "@koclukpanel/ui";
import {
  ApiHatasi,
  degisiklikEkHak,
  degisiklikKarar,
  degisiklikTalepleri,
  ogrenciListesi,
  type DegisiklikTalebi,
} from "./api";

/**
 * PROGRAM DEĞİŞİKLİK TALEPLERİ — F-SCH-11
 *
 * ================================================================
 * AKIŞ TEK YÖNDE KIRIKTI
 * ================================================================
 * Öğrenci uygulamasından talep AÇILABİLİYORDU (`POST
 * /program/degisiklik`) ama panelde talebi karara bağlayacak hiçbir
 * ekran yoktu. Yani öğrenci istiyor, kimse cevaplayamıyordu.
 *
 * ================================================================
 * KARAR BİR KEZ VERİLİR
 * ================================================================
 * Sunucu karara bağlanmış bir talebi tekrar karara bağlamıyor: ikinci
 * karar birincinin izini silmeden üstüne yazardı ve öğrenci hangi
 * cevabın geçerli olduğunu bilemezdi. Ekran da karar verilmiş
 * satırlarda düğme göstermiyor.
 */

/**
 * `ChangeRequestStatus` adlari — 360 ekraninin Talepler sekmesi de
 * kullaniyor. `StudentRequest` durumlariyla AYNI DEGIL (`ACCEPTED` ve
 * `PARTIALLY_ACCEPTED` yalnizca burada var); tek bir tabloyla iki enum
 * karsilamak, ikisinden birinin degerlerini ham gostermek demekti.
 */
export const DURUM_ADI: Record<string, string> = {
  PENDING: "Bekliyor",
  ACCEPTED: "Kabul",
  PARTIALLY_ACCEPTED: "Kısmen kabul",
  REJECTED: "Ret",
  CANCELLED: "İptal",
};

export const DURUM_TONU: Record<string, "notr" | "bilgi" | "onay" | "uyari" | "hata"> = {
  PENDING: "uyari",
  ACCEPTED: "onay",
  PARTIALLY_ACCEPTED: "bilgi",
  REJECTED: "hata",
  CANCELLED: "notr",
};

/** Öğrencinin verdiği sebep — `ChangeReason`. */
export const NEDEN_ADI: Record<string, string> = {
  SCHOOL_LOAD: "Okul yoğunluğu",
  HEALTH: "Sağlık",
  FAMILY: "Ailevi",
  DID_NOT_UNDERSTAND: "Anlamadım",
  RESOURCE_ISSUE: "Kaynak sorunu",
  TOO_HEAVY: "Program ağır",
  TOO_LIGHT: "Program hafif",
  OTHER: "Diğer",
};

/** Karara bağlanabilecek hedefler — `CANCELLED` öğrencinin işi. */
const KARAR_SECENEKLERI = ["ACCEPTED", "PARTIALLY_ACCEPTED", "REJECTED"] as const;

const tarih = (d: string): string => new Date(d).toLocaleDateString("tr-TR");

export function DegisiklikTalepleri() {
  const [durum, setDurum] = useState<string>("PENDING");
  const [acik, setAcik] = useState<string | null>(null);
  const [ekHakAcik, setEkHakAcik] = useState(false);

  const liste = useQuery({
    queryKey: ["degisiklik-talepleri", durum],
    queryFn: () => degisiklikTalepleri(durum || undefined),
    retry: false,
  });

  /**
   * ================================================================
   * ÖĞRENCİ ADI AYRI SORGUDAN
   * ================================================================
   * Talep listesi yalnızca `studentId` döndürüyor. "Öğrenci
   * cms7iz0by…" yazan bir kuyruk kullanılamaz — uzman kimin talebine
   * baktığını bilmek zorunda.
   *
   * Ad öğrenci listesinden eşleştiriliyor. İki sorgunun kapsamı AYNI
   * (ikisi de uzmanın kendi öğrencileri), o yüzden eşleşmeyen bir
   * kimlik "kapsam dışı" demek — o durumda kısaltılmış kimlik
   * gösteriliyor, uydurma bir ad değil.
   */
  const ogrenciler = useQuery({
    queryKey: ["ogrenciler"],
    queryFn: ogrenciListesi,
    retry: false,
  });

  const adlar = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of ogrenciler.data?.ogrenciler ?? []) m.set(o.id, `${o.ad} ${o.soyad}`);
    return m;
  }, [ogrenciler.data]);

  const ad = (t: DegisiklikTalebi): string => adlar.get(t.studentId) ?? `#${t.studentId.slice(-6)}`;

  if (liste.isPending) return <Iskelet satir={4} />;

  if (liste.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Talepler alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <Secim
          etiket="Durum"
          value={durum}
          onChange={(e) => setDurum(e.target.value)}
          className="w-56"
        >
          <option value="">Hepsi</option>
          {Object.entries(DURUM_ADI).map(([d, a]) => (
            <option key={d} value={d}>
              {a}
            </option>
          ))}
        </Secim>
        <div className="flex items-center gap-3">
          <span className="text-notr-600 text-sm">{liste.data.toplam} talep</span>
          <Buton gorunum="ikincil" onClick={() => setEkHakAcik(true)}>
            Ek hak tanı
          </Buton>
        </div>
      </div>

      {ekHakAcik && (
        <EkHakFormu
          ogrenciSecenekleri={[...adlar].map(([id, ad]) => ({ id, ad }))}
          onKapat={() => setEkHakAcik(false)}
        />
      )}

      {liste.data.talepler.length === 0 ? (
        <BosDurum
          ikon={durum === "PENDING" ? "tamam" : "bos"}
          baslik={durum === "PENDING" ? "Karar bekleyen talep yok" : "Talep yok"}
          aciklama={
            durum === "PENDING"
              ? "Öğrencilerden gelen program değişiklik talepleri burada birikir."
              : `"${DURUM_ADI[durum] ?? "Seçili"}" durumunda talep bulunmuyor.`
          }
        />
      ) : (
        <Tablo
          satirlar={liste.data.talepler}
          anahtar={(t) => t.id}
          kartBasligi={0}
          sutunlar={[
            { baslik: "Öğrenci", mobil: true, hucre: ad, className: "font-medium" },
            { baslik: "Sebep", mobil: true, hucre: (t) => NEDEN_ADI[t.reason] ?? t.reason },
            { baslik: "Tarih", mobil: true, hucre: (t) => tarih(t.createdAt) },
            {
              baslik: "Hak",
              hucre: (t) =>
                /*
                  EK HAK ayrı gösteriliyor: öğrencinin aylık tek hakkı
                  mı, yoksa uzmanın tanıdığı ek bir yer mi — kararı
                  veren kişi bunu bilmeli.

                  `grantIndex` SAYISI GÖSTERİLMİYOR. İlk yazımda
                  `{grantIndex}.` yazıyordum ve ekranda "0." çıkıyordu:
                  0 tabanlı bir dizin, kullanıcıya hiçbir şey
                  anlatmıyor. Anlamı taşıyan tek bilgi hakkın AYLIK mı
                  EK mi olduğu.
                */
                t.isExtraGrant ? <Rozet ton="bilgi">Ek hak</Rozet> : <span>Aylık</span>,
            },
            {
              baslik: "Durum",
              mobil: true,
              hucre: (t) => (
                <Rozet ton={DURUM_TONU[t.status] ?? "notr"}>
                  {DURUM_ADI[t.status] ?? t.status}
                </Rozet>
              ),
            },
            {
              baslik: "İşlem",
              // Telefonda da gorunur: isaretlenmeseydi `Tablo` bu
              // sutunu dar ekranda gizler ve satirin TEK islemi
              // telefondan erisilemez olurdu (07.08.2026, 375px).
              mobil: true,
              hucre: (t) => (
                <div className="flex justify-end">
                  {/*
                    KARAR VERİLMİŞ TALEPTE DÜĞME YOK. Sunucu ikinci
                    kararı reddediyor; düğmeyi göstermek basılıp hata
                    alınacak bir vaat olurdu.
                  */}
                  <Buton gorunum="sessiz" onClick={() => setAcik(acik === t.id ? null : t.id)}>
                    {acik === t.id ? "Kapat" : t.status === "PENDING" ? "Karara bağla" : "Gör"}
                  </Buton>
                </div>
              ),
            },
          ]}
          altSatir={(t) =>
            acik === t.id ? (
              <div className="border-notr-100 bg-notr-50 border-b px-3 py-3">
                <TalepAyrintisi talep={t} ogrenciAdi={ad(t)} durumSuzgeci={durum} />
              </div>
            ) : null
          }
        />
      )}
    </div>
  );
}

function TalepAyrintisi({
  talep,
  ogrenciAdi,
  durumSuzgeci,
}: {
  talep: DegisiklikTalebi;
  ogrenciAdi: string;
  durumSuzgeci: string;
}) {
  const qc = useQueryClient();
  const [hedef, setHedef] = useState<string>("ACCEPTED");
  const [cevap, setCevap] = useState("");

  const karar = useMutation({
    mutationFn: () => degisiklikKarar(talep.id, hedef, cevap.trim() || null),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["degisiklik-talepleri", durumSuzgeci] });
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-notr-800 text-sm font-medium">
          {ogrenciAdi} — {NEDEN_ADI[talep.reason] ?? talep.reason}
        </p>
        {/*
          ÖĞRENCİNİN KENDİ CÜMLESİ. Karar bunun üzerine veriliyor;
          özetlemek ya da kısaltmak, kararı veren kişiyi eksik bilgiyle
          bırakırdı.
        */}
        <p className="text-notr-700 mt-1 text-sm whitespace-pre-wrap">{talep.reasonText}</p>
      </div>

      {talep.status === "PENDING" ? (
        <div className="flex flex-col gap-3">
          <Secim etiket="Karar" value={hedef} onChange={(e) => setHedef(e.target.value)}>
            {KARAR_SECENEKLERI.map((d) => (
              <option key={d} value={d}>
                {DURUM_ADI[d]}
              </option>
            ))}
          </Secim>

          <MetinAlani
            etiket="Öğrenciye cevap"
            rows={3}
            value={cevap}
            onChange={(e) => setCevap(e.target.value)}
            yardim="Öğrenci bu metni görecek. Reddediyorsanız sebebini yazın."
          />

          {karar.isError && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {karar.error instanceof ApiHatasi ? karar.error.message : "Karar kaydedilemedi."}
            </p>
          )}

          <div>
            <Buton yukleniyor={karar.isPending} onClick={() => karar.mutate()}>
              Kararı kaydet
            </Buton>
            <p className="text-notr-600 mt-1.5 text-xs">
              Karar bir kez verilir; kaydedildikten sonra değiştirilemez.
            </p>
          </div>
        </div>
      ) : (
        <div className="text-notr-700 flex flex-col gap-1 text-sm">
          <p>
            <span className="font-medium">Karar: </span>
            {DURUM_ADI[talep.status] ?? talep.status}
            {talep.decidedAt && ` · ${tarih(talep.decidedAt)}`}
          </p>
          {talep.coachResponse && (
            <p className="whitespace-pre-wrap">
              <span className="font-medium">Cevap: </span>
              {talep.coachResponse}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * EK DEĞİŞİKLİK HAKKI TANIMA — matris §5.6, F-SCH-11
 *
 * `POST /program/degisiklik/ek-hak` yazılıydı, hiçbir ekran
 * çağırmıyordu. Öğrencinin aylık tek hakkı doluyken gerçekten haklı
 * bir durumu olduğunda (hastalık, ailevi sebep) uzmanın yapabileceği
 * hiçbir şey yoktu: hak kapalıydı ve açmanın yolu panelde yoktu.
 *
 * ================================================================
 * UZMAN ÖĞRENCİNİN ADINA TALEP AÇMIYOR — BOŞ BİR YER AÇIYOR
 * ================================================================
 * Sunucunun açıklaması bu ve ekran da öyle anlatıyor. Fark önemli:
 * talebin GEREKÇESİ öğrencinin sözü olmalı. Uzman gerekçeyi de
 * yazsaydı, öğrencinin ağzından bir kayıt üretilmiş olurdu.
 *
 * Buradaki gerekçe UZMANIN gerekçesi: "neden ek hak tanıdım".
 * Denetim kaydına o yazılıyor.
 */
function EkHakFormu({
  ogrenciSecenekleri,
  onKapat,
}: {
  ogrenciSecenekleri: Array<{ id: string; ad: string }>;
  onKapat: () => void;
}) {
  const qc = useQueryClient();
  const [ogrenciId, setOgrenciId] = useState("");
  const [gerekce, setGerekce] = useState("");
  const [bitti, setBitti] = useState(false);

  const mut = useMutation({
    mutationFn: () => degisiklikEkHak(ogrenciId, gerekce.trim()),
    onSuccess: async () => {
      setBitti(true);
      await qc.invalidateQueries({ queryKey: ["degisiklik-talepleri"] });
    },
  });

  if (bitti) {
    return (
      <Kart baslik="Ek hak tanındı">
        <div className="flex flex-col gap-3">
          <p className="text-notr-700 text-sm">
            Öğrenci artık bu ay bir değişiklik talebi daha açabilir. Talebi{" "}
            <strong>öğrenci kendisi</strong> açacak; siz yalnızca yer açtınız.
          </p>
          <div>
            <Buton onClick={onKapat}>Kapat</Buton>
          </div>
        </div>
      </Kart>
    );
  }

  return (
    <Kart
      baslik="Ek değişiklik hakkı tanı"
      aciklama="Öğrencinin aylık hakkı dolduysa bir yer daha açar. Talebi öğrenci kendisi açar."
    >
      <div className="flex flex-col gap-4">
        {ogrenciSecenekleri.length === 0 ? (
          <p className="text-notr-600 text-sm">
            Kapsamınızda öğrenci görünmüyor; ek hak tanınacak kimse yok.
          </p>
        ) : (
          <>
            <Secim
              etiket="Öğrenci"
              value={ogrenciId}
              onChange={(e) => setOgrenciId(e.target.value)}
            >
              <option value="">Seçiniz</option>
              {ogrenciSecenekleri.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.ad}
                </option>
              ))}
            </Secim>

            <MetinAlani
              etiket="Gerekçeniz"
              rows={3}
              value={gerekce}
              onChange={(e) => setGerekce(e.target.value)}
              yardim="Denetim kaydına yazılır. Öğrencinin talep gerekçesi değil, sizin ek hak tanıma sebebiniz."
            />
          </>
        )}

        {mut.isError && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {mut.error instanceof ApiHatasi ? mut.error.message : "Ek hak tanınamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={mut.isPending}
            disabled={ogrenciId === "" || gerekce.trim().length < 3}
            onClick={() => mut.mutate()}
          >
            Ek hakkı tanı
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}
